import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InsightsDailyRevenuePoint } from "@shared-core/data/api-types";
import { DailyRevenueChart } from "./DailyRevenueChart";

// The Daily tab: loads the composed /views/insights/daily-revenue series and
// renders the booked/invoiced revenue+contribution chart at daily grain. Covers
// loading/empty/error, mirroring the Monthly-tab template.

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; days: InsightsDailyRevenuePoint[] };

// `tradingCompany` is a code chosen in the page header's Trading company
// control; absent is the group. Changing it refetches the series.
export function DailyRevenueTab({
  tradingCompany,
}: {
  tradingCompany?: string;
}) {
  const adapter = useDataAdapter();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const result = await adapter.getInsightsDailyRevenue(
        undefined,
        tradingCompany,
      );
      setState({ status: "ready", days: result.days });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter, tradingCompany]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
        Loading daily revenue…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
        <p className="text-destructive text-sm">
          Couldn’t load daily revenue. Please try again.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return <DailyRevenueChart days={state.days} />;
}
